import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, RegisterPayload } from '../services/authService';
import { registerApiAuth, type EsitoRefresh } from '../services/api';
import * as sessione from '../services/sessione';
import type { UserProfile } from '../types';

interface AuthContextValue {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<UserProfile>;
  register: (data: RegisterPayload) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: UserProfile['ruolo'][]) => boolean;
  /**
   * `false` solo per un sotto-utente `vedi-sito`, il cui ruolo promette
   * esplicitamente "NON aggiunge prodotti al carrello". Applicato anche
   * lato server (`PUT /api/clienti/me/cart`): questo serve a non far
   * *sembrare* possibile un'azione che verrà rifiutata.
   */
  canAddToCart: boolean;
  /**
   * `false` per i sotto-utenti `vedi-sito` e `crea-carrello`: possono
   * preparare il carrello ma non inviare l'ordine. Applicato anche lato
   * server (`POST /api/orders`).
   */
  canPlaceOrder: boolean;
  /**
   * Aprire un reso: titolare o operatore abilitato al carrello, mai in
   * impersonazione. Applicato anche lato server (`can_open_rma`).
   */
  canOpenRma: boolean;
  /**
   * Swap dei token correnti — usato dal flusso impersonazione admin.
   * Salva il nuovo access/refresh token e ricarica il profilo ``/me``.
   * Il chiamante è responsabile di backuppare/ripristinare i token
   * originali (es. ``AdminContext.startImpersonation``).
   */
  applyTokens: (accessToken: string, refreshToken: string) => Promise<UserProfile>;
  /**
   * Rilegge il profilo da ``/me`` con il token corrente. Serve quando lo
   * stato dell'account cambia lato server: il primo ordine di un Contatto
   * crea il Cliente e ``contatto`` torna falso. Se la lettura fallisce il
   * profilo resta quello di prima e la funzione restituisce ``null``.
   */
  ricaricaUtente: () => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const accessTokenRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const getToken = useCallback(() => accessTokenRef.current, []);
  const refreshInVolo = useRef<Promise<EsitoRefresh> | null>(null);

  /** Fine della sessione in questa scheda, senza toccare il server: dopo un
   *  logout o quando un'altra scheda l'ha chiusa. */
  const fineSessione = useCallback(() => {
    accessTokenRef.current = null;
    sessione.cancella();
    setUser(null);
    navigate('/accedi', { replace: true });
  }, [navigate]);

  /** Logout chiesto dall'utente: chiude la sessione sul server (anche le
   *  impersonazioni aperte da lei) e in tutte le schede che la condividono. */
  const doLogout = useCallback(() => {
    const token = sessione.tokenProprio();
    if (token) authService.logout(token).catch(() => {});
    fineSessione();
    sessione.annunciaUscita(sessione.sidDi(token));
  }, [fineSessione]);

  /** Un solo refresh alla volta: le richieste che ricevono 401 insieme
   *  aspettano lo stesso. Il token si toglie solo se il server lo rifiuta,
   *  non per un errore di rete. */
  const doRefresh = useCallback((): Promise<EsitoRefresh> => {
    if (refreshInVolo.current) return refreshInVolo.current;
    const p = (async (): Promise<EsitoRefresh> => {
      const stored = sessione.leggi();
      if (!stored) return 'scaduta';
      try {
        const res = await authService.refresh(stored);
        accessTokenRef.current = res.data.access_token;
        sessione.salva(res.data.refresh_token);
        return 'ok';
      } catch (e: any) {
        if (e?.response?.status === 401) return 'scaduta';
        return 'errore';
      } finally {
        refreshInVolo.current = null;
      }
    })();
    refreshInVolo.current = p;
    return p;
  }, []);

  // Register token getter + refresh + logout with the axios instance
  useEffect(() => {
    registerApiAuth(getToken, doRefresh, fineSessione);
  }, [getToken, doRefresh, fineSessione]);

  // Le altre schede: chi chiede la sessione la riceve, e se la nostra viene
  // chiusa altrove si esce anche qui.
  useEffect(() => sessione.ascolta(() => {
    if (accessTokenRef.current || sessione.leggi()) fineSessione();
  }), [fineSessione]);

  // Restore session on cold boot: dal token di questa scheda o, per una
  // scheda appena aperta, da quello di un'altra scheda del portale.
  useEffect(() => {
    const restore = async () => {
      if (!sessione.leggi()) {
        const altrui = await sessione.chiediAlleAltreSchede();
        if (altrui) sessione.salva(altrui);
      }
      if (!sessione.leggi()) {
        setIsLoading(false);
        return;
      }
      try {
        if ((await doRefresh()) === 'ok') {
          const meRes = await authService.me();
          setUser(meRes.data);
        } else {
          sessione.cancella();
        }
      } catch {
        sessione.cancella();
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string) => {
    const res = await authService.login(email, password);
    accessTokenRef.current = res.data.access_token;
    sessione.cancella();
    sessione.salva(res.data.refresh_token);
    const meRes = await authService.me();
    setUser(meRes.data);
    return meRes.data;
  }, []);

  const register = useCallback(async (data: RegisterPayload) => {
    await authService.register(data);
  }, []);

  const applyTokens = useCallback(async (accessToken: string, refreshToken: string) => {
    accessTokenRef.current = accessToken;
    sessione.salva(refreshToken);
    const meRes = await authService.me();
    setUser(meRes.data);
    return meRes.data;
  }, []);

  const ricaricaUtente = useCallback(async () => {
    try {
      const meRes = await authService.me();
      setUser(meRes.data);
      return meRes.data;
    } catch {
      return null;
    }
  }, []);

  const hasRole = useCallback(
    (...roles: UserProfile['ruolo'][]) => !!user && roles.includes(user.ruolo),
    [user],
  );

  // Poteri dei sotto-utenti. Un operatore con `ruoloOperatore` assente
  // (documento legacy) ricade su "carrello sì, ordine no", stessa regola
  // di `can_modify_cart` / `can_place_order` nel backend.
  const isSubordinato = user?.ruolo === 'cliente_subordinato';
  const canAddToCart = !isSubordinato || user?.ruoloOperatore !== 'vedi-sito';
  // Solo l'account del Cliente ordina: titolare o operatore `crea-ordine`,
  // mai in impersonazione.
  const canPlaceOrder =
    !user?.impersonatoDa &&
    (user?.ruolo === 'cliente' || (isSubordinato && user?.ruoloOperatore === 'crea-ordine'));
  const canOpenRma =
    !user?.impersonatoDa && (user?.ruolo === 'cliente' || (isSubordinato && canAddToCart));

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout: doLogout,
        hasRole,
        canAddToCart,
        canPlaceOrder,
        canOpenRma,
        applyTokens,
        ricaricaUtente,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
